import { useState, useMemo } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Settings, Monitor, Cpu } from "lucide-react";

const CATS = [
  { id: "process", label: "Prozessoptimierung", icon: Settings, saving: 0.30, color: "#D4A373" },
  { id: "digital", label: "Digitalisierung", icon: Monitor, saving: 0.40, color: "#3B82F6" },
  { id: "ai", label: "KI / Automatisierung", icon: Cpu, saving: 0.55, color: "#8B5CF6" },
];

function formatCHF(n) { return `CHF ${Math.round(n).toLocaleString("de-CH")}`; }

export default function ROITeaserSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [category, setCategory] = useState("process");
  const [employees, setEmployees] = useState(5);
  const [hoursWeek, setHoursWeek] = useState(10);

  const cat = CATS.find(c => c.id === category);
  const result = useMemo(() => {
    const hpy = employees * hoursWeek * 48;
    const saved = Math.round(hpy * cat.saving);
    const cost = Math.round(saved * 85);
    return { saved, cost };
  }, [employees, hoursWeek, cat]);

  return (
    <section ref={ref} className="py-20 bg-[#0F172A]" data-testid="roi-teaser">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center"
        >
          {/* Left: Text + Inputs */}
          <div>
            <div className="text-[0.6rem] font-bold uppercase text-[#D4A373] mb-3" style={{ letterSpacing: "0.2em" }}>
              ROI-Rechner
            </div>
            <h2 className="font-playfair text-2xl sm:text-3xl font-semibold text-white mb-3">
              Was kostet Sie der Status Quo?
            </h2>
            <p className="text-sm text-[#94A3B8] mb-8">
              Berechnen Sie in Sekunden, wie viel Ihr Unternehmen durch Optimierung einsparen kann.
            </p>

            {/* Category pills */}
            <div className="flex gap-2 mb-6 flex-wrap">
              {CATS.map(c => {
                const CatIcon = c.icon;
                return (
                  <button key={c.id} onClick={() => setCategory(c.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border transition-all ${
                      category === c.id
                        ? "text-white border-transparent"
                        : "text-[#94A3B8] border-[#334155] hover:border-[#475569]"
                    }`}
                    style={category === c.id ? { backgroundColor: c.color, borderColor: c.color } : {}}>
                    <CatIcon className="w-3 h-3" /> {c.label}
                  </button>
                );
              })}
              <Link to="/roi-rechner"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#D4A373] border border-[#334155] hover:border-[#D4A373] transition-all">
                +2 mehr <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {/* Sliders */}
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-xs text-[#94A3B8] mb-1">
                  <span>Mitarbeitende</span>
                  <span className="text-white font-semibold">{employees}</span>
                </div>
                <input type="range" min={1} max={30} step={1} value={employees}
                  onChange={e => setEmployees(Number(e.target.value))}
                  className="w-full h-1 bg-[#334155] rounded-none appearance-none cursor-pointer accent-[#D4A373]" />
              </div>
              <div>
                <div className="flex justify-between text-xs text-[#94A3B8] mb-1">
                  <span>Stunden/Woche manuelle Aufgaben</span>
                  <span className="text-white font-semibold">{hoursWeek}h</span>
                </div>
                <input type="range" min={1} max={30} step={1} value={hoursWeek}
                  onChange={e => setHoursWeek(Number(e.target.value))}
                  className="w-full h-1 bg-[#334155] rounded-none appearance-none cursor-pointer accent-[#D4A373]" />
              </div>
            </div>
          </div>

          {/* Right: Result */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/10 border border-[#475569] backdrop-blur-sm p-8 text-center"
          >
            <div className="text-[0.6rem] font-bold uppercase text-[#94A3B8] mb-1" style={{ letterSpacing: "0.15em" }}>
              Geschätzte Einsparung pro Jahr
            </div>
            <motion.div
              key={`${category}-${employees}-${hoursWeek}`}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="font-playfair text-5xl font-semibold text-[#10B981] my-4"
            >
              {formatCHF(result.cost)}
            </motion.div>
            <div className="text-sm text-[#94A3B8] mb-1">
              <span className="text-white font-semibold">{result.saved.toLocaleString("de-CH")} Stunden</span> weniger manueller Aufwand
            </div>
            <div className="text-xs text-[#64748B] mb-6">
              bei {Math.round(cat.saving * 100)}% Effizienzgewinn ({cat.label})
            </div>

            <Link to="/roi-rechner"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4A373] text-white text-sm font-semibold hover:bg-[#C4956A] transition-colors"
              data-testid="roi-teaser-cta">
              Detaillierte Analyse starten <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
